/**
 * Page « Abonnements » d'un parent : les PROGRAMMES de SKILLCARDS par niveau
 * (grille de prix en base, `get_skillcard_price_grid`). Puces de niveau
 * (ouvertes sur celui d'un enfant lié), programmes du niveau avec leur prix.
 * Enfant lié de ce niveau → achat direct pour lui (`for_student_id`, retour sur
 * « SKILLCARDS pour mes enfants ») ; sinon « Offrir » → carte cadeau (code à
 * partager, « Mes cadeaux »). Web seulement (App Store 3.1.1).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@jeraisonne/ui';
import { cardPalette, offerContentLabel } from '@jeraisonne/db/cards';
import { levelLabel, subjectLabel } from '@jeraisonne/db/curriculum';
import { supabase } from '@/lib/supabase';
import {
  skillcardCheckoutErrorMessage,
  startSkillcardCheckout,
  useSkillcardPriceGrid,
} from '@/lib/skillcards/purchase';
import {
  childFirstName,
  childForLevel,
  defaultProgrammeLevel,
  programmeLevels,
  type LevelledChild,
} from '@/lib/parent/programme-levels';
import { SkillcardOfferRow } from './skillcard-offer-row';

/** Niveau de chaque enfant lié (RLS `student_profiles: parent_can_read`). */
function useChildrenLevels(
  children: ReadonlyArray<{ child_id: string; display_name: string | null }>,
) {
  const ids = children.map((c) => c.child_id).sort();
  return useQuery({
    queryKey: ['parent-children-levels', ids],
    enabled: ids.length > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<Record<string, string | null>> => {
      const { data, error } = await supabase
        .from('student_profiles')
        .select('id, level')
        .in('id', ids);
      if (error) throw new Error(error.message);
      return Object.fromEntries(
        (data ?? []).map((r) => [r.id, (r.level as string | null) ?? null]),
      );
    },
  });
}

export function ParentProgrammesSection({
  linkedChildren,
}: {
  linkedChildren: ReadonlyArray<{ child_id: string; display_name: string | null }>;
}) {
  const grid = useSkillcardPriceGrid();
  const levelsById = useChildrenLevels(linkedChildren);
  const kids: LevelledChild[] = linkedChildren.map((c) => ({
    child_id: c.child_id,
    display_name: c.display_name,
    level: levelsById.data?.[c.child_id] ?? null,
  }));
  const programmes = grid.data?.programmes ?? [];
  const levels = programmeLevels(programmes);
  const [picked, setPicked] = useState<string | null>(null);
  const level = picked ?? defaultProgrammeLevel(levels, kids);
  const child = childForLevel(kids, level);
  const shown = programmes.filter((p) => p.level === level);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function buy(targetId: string, cardId: string | undefined) {
    if (!cardId) return;
    setError(null);
    setBusy(targetId);
    try {
      window.location.href = await startSkillcardCheckout({
        kind: 'programme',
        targetId,
        cardId,
        ...(child ? { forStudentId: child.child_id } : { gift: true }),
      });
    } catch (e) {
      setError(skillcardCheckoutErrorMessage((e as Error).message));
      setBusy(null);
    }
  }

  if (grid.isLoading) return <div className="bg-ink-100 h-32 animate-pulse rounded-2xl" />;
  if (levels.length === 0) return null;

  const palette = level ? cardPalette([level]) : null;
  return (
    <section className="space-y-3" aria-labelledby="programmes-niveau">
      <div>
        <h2
          id="programmes-niveau"
          className="font-display text-ink-700 text-sm font-bold uppercase tracking-wider"
        >
          Programmes par niveau
        </h2>
        <p className="text-ink-500 mt-1 text-sm">
          Toutes les SKILLCARDS d’une année et leurs exercices, en un seul paiement (accès{' '}
          {grid.data?.settings.validity_months ?? 12} mois, sans abonnement).
        </p>
      </div>

      <div role="tablist" aria-label="Niveau" className="flex flex-wrap gap-2">
        {levels.map((l) => {
          const on = l === level;
          return (
            <button
              key={l}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setPicked(l)}
              className={cn(
                'min-h-[2.75rem] rounded-full border px-4 text-sm font-semibold active:scale-[0.98]',
                on
                  ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-ink-200 text-ink-700 ocean:bg-white/5 bg-white',
              )}
            >
              {levelLabel(l)}
            </button>
          );
        })}
      </div>

      {shown.map((p) => (
        <SkillcardOfferRow
          key={p.target_id}
          quote={p}
          title={`${subjectLabel(p.subject ?? '')} · ${levelLabel(p.level ?? '')}`}
          subtitle={offerContentLabel(p)}
          highlight={child ? `Pour ${childFirstName(child)}` : 'Carte cadeau à offrir'}
          buyLabel={child ? `Pour ${childFirstName(child)}` : 'Offrir'}
          accent={palette?.accent}
          background={palette?.bg}
          hideDeductionNote
          busy={busy === p.target_id}
          disabled={!!busy || p.card_ids.length === 0}
          onBuy={() => void buy(p.target_id, p.card_ids[0])}
        />
      ))}

      <p className="text-ink-500 text-xs">
        {child
          ? `Les SKILLCARDS arrivent dans le deck de ${childFirstName(child)}, qui en est prévenu. Celles qu’il possède déjà sont déduites au paiement.`
          : 'Aucun enfant relié à ce niveau : tu reçois un code cadeau à lui donner (« Mes cadeaux »).'}
      </p>
      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}
    </section>
  );
}
